import { useParams } from 'react-router'
import {
  Form,
  Input,
  Button,
  addToast,
  ModalHeader,
  ModalBody,
  Tooltip
} from '@heroui/react'
import type { PbBranding } from '@itsrever/rever-api'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { DownloadIcon } from '@/ui/icons/download'
import { UploadIcon } from '@/ui/icons/upload'
import { setTheming } from '@/redux/api/settings-api-slice'

const schema = z.object({
  slug: z.string(),
  ecommerceId: z.string(),
  logo_url: z
    .url({ message: 'Please enter a valid URL' })
    .optional()
    .or(z.literal('')),
  background_image_url: z
    .url({ message: 'Please enter a valid URL' })
    .optional()
    .or(z.literal('')),
  favicon_url: z
    .url({ message: 'Please enter a valid URL' })
    .optional()
    .or(z.literal('')),
  background_text_color: z.string().optional(),
  title: z.string().optional()
})

type SettingsData = z.infer<typeof schema>

interface DemoSettingsFormProps {
  onClose: () => void
  onThemeUpdate: (theme: Partial<PbBranding>) => void
}

export const DemoSettingsForm = ({
  onClose,
  onThemeUpdate
}: DemoSettingsFormProps) => {
  const { slug } = useParams()

  const dispatch = useAppDispatch()

  const ecommerceId = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_id
  )

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      slug: z.string().parse(slug),
      ecommerceId: z.string().parse(ecommerceId)
    }
  })

  const uploadSettings = (file: File) => {
    const fileReader = new FileReader()

    fileReader.readAsText(file)

    fileReader.onloadend = () => {
      try {
        const data: SettingsData = JSON.parse(fileReader.result as string)

        Object.entries(data).forEach(([name, value]) =>
          setValue(name as keyof SettingsData, value)
        )
      } catch {
        addToast({
          title: 'Failed to upload',
          description: 'Failed to upload the file.',
          color: 'danger'
        })
      }
    }
  }

  const downloadSettings = () => {
    const jsonString = `data:text/json;chatset=utf-8,${encodeURIComponent(
      JSON.stringify(getValues())
    )}`

    const link = document.createElement('a')
    link.href = jsonString
    link.setAttribute('download', `${slug}.json`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const saveSettings = ({
    logo_url,
    background_image_url,
    background_text_color,
    favicon_url,
    title
  }: SettingsData) => {
    // Only submit dirty fields to avoid overwriting the default config with empty values
    const data = {
      ...(logo_url && { logo_url }),
      ...(favicon_url && { favicon_url }),
      ...(background_image_url && {
        background_image_url
      }),
      ...(background_text_color && {
        background_text_color
      }),
      ...(title && { title })
    }

    dispatch(setTheming(data))

    onThemeUpdate(data)
    onClose()
  }

  return (
    <>
      <ModalHeader className="flex items-center gap-4 font-[750]">
        Edit Settings{' '}
        <span className="inline-flex gap-2">
          <Tooltip content="Upload">
            <label className="box-border inline-flex size-8 min-w-8 cursor-pointer select-none appearance-none items-center justify-center overflow-hidden whitespace-nowrap rounded-small border-medium border-default bg-transparent text-foreground subpixel-antialiased outline-none transition-transform-colors-opacity hover:opacity-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus motion-reduce:transition-none">
              <input
                type="file"
                name="file"
                className="sr-only"
                accept="application/json"
                onChange={(e) => {
                  const target = e.target as HTMLInputElement

                  if (target.files && (target.files as FileList).length > 0) {
                    uploadSettings(target.files[0])
                  }
                }}
              />
              <UploadIcon className="size-5" />
            </label>
          </Tooltip>
          <Tooltip content="Download">
            <Button
              type="button"
              variant="bordered"
              size="sm"
              radius="sm"
              onPress={downloadSettings}
              isIconOnly
              aria-label="Download settings"
            >
              <DownloadIcon className="size-5" />
            </Button>
          </Tooltip>
        </span>
      </ModalHeader>
      <ModalBody className="pb-5">
        <Form
          id="settings"
          className="w-full space-y-1.5"
          onSubmit={handleSubmit(saveSettings)}
        >
          <div className="flex w-full justify-between gap-4">
            <Input
              {...register('slug')}
              isDisabled
              type="text"
              label="Slug"
              placeholder="Slug"
              radius="sm"
              variant="bordered"
              classNames={{
                inputWrapper: 'border-solid'
              }}
            />
            <Input
              {...register('ecommerceId')}
              isDisabled
              type="text"
              label="Ecommerce Id"
              placeholder="Ecommerce Id"
              radius="sm"
              variant="bordered"
              classNames={{
                inputWrapper: 'border-solid'
              }}
            />
          </div>
          <Input
            {...register('logo_url')}
            label="Logo URL"
            placeholder="example.com"
            type="text"
            radius="sm"
            isInvalid={!!errors.logo_url}
            errorMessage={errors.logo_url?.message}
            variant="bordered"
            classNames={{
              inputWrapper: 'border-solid'
            }}
          />
          <Input
            {...register('background_image_url')}
            label="Background Image URL"
            placeholder="example.com"
            type="text"
            radius="sm"
            isInvalid={!!errors.background_image_url}
            errorMessage={errors.background_image_url?.message}
            variant="bordered"
            classNames={{
              inputWrapper: 'border-solid'
            }}
          />
          <Input
            {...register('favicon_url')}
            label="Favicon URL"
            placeholder="example.com"
            type="text"
            radius="sm"
            isInvalid={!!errors.favicon_url}
            errorMessage={errors.favicon_url?.message}
            variant="bordered"
            classNames={{
              inputWrapper: 'border-solid'
            }}
          />
          <Input
            {...register('background_text_color')}
            label="Background Text Color"
            placeholder="#000000"
            type="text"
            radius="sm"
            variant="bordered"
            classNames={{
              inputWrapper: 'border-solid'
            }}
          />
          <Input
            {...register('title')}
            label="Title"
            placeholder="Title"
            type="text"
            radius="sm"
            variant="bordered"
            classNames={{
              inputWrapper: 'border-solid'
            }}
          />
          <div className="flex w-full justify-end gap-2">
            <Button
              type="button"
              variant="bordered"
              color="secondary"
              radius="sm"
              onPress={onClose}
            >
              Cancel
            </Button>
            <Button
              form="settings"
              type="submit"
              color="primary"
              variant="solid"
              radius="sm"
              className="bg-primary-main"
            >
              Save Settings
            </Button>
          </div>
        </Form>
      </ModalBody>
    </>
  )
}
